import React from 'react';

import jwt_decode from 'jwt-decode';

import { Authentication } from '@pitaman71/omniglot-live-data';
import { Button, Dialog, DialogActions, DialogTitle, Typography } from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';

import { GoogleCredentialResponse, GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';

import * as XLayout from '@pitaman71/react-explicit-layout';
import * as Debug from '../Debug';

const __moduleName__ = 'omniglot-live-react.ViewIdentity';

export const AuthContext = React.createContext<undefined|Authentication.Interface<JSX.Element>>(undefined);

interface Config {
    services: {
        "com.apple"?: {
            auth: {
                clientId: string,
                teamId: string,
                keyId: string,
                scope: string
            }
        },
        "com.google"?: {
            auth: {
                clientId: string
            }
        },
        "app.swivell": {
            "backend": {
                baseURI: string
            }
        }
    }
}

const accessTokenStorage = 'zyxwvut';

export function Session(props: React.PropsWithChildren<{
    config: Config,
    storage: { getItem: (key:string) => Promise<string|null>, setItem: (key:string, value:string) => Promise<any>, removeItem: (key: string) => Promise<any> }
    masquerade?: Partial<Authentication.Credentials>
}>) {
    const [ state, setState ] = React.useState<{ 
        credentials: Partial<Authentication.Credentials>,
        mode: Authentication.Modes
    }>({
        credentials: {},
        mode: 'check'
    });
    const [ why, setWhy ] = React.useState<undefined|JSX.Element>(undefined);

    React.useEffect(() => {
        props.storage.getItem(accessTokenStorage).then(text => {
            if(text) {
                setState({ credentials: JSON.parse(text), mode: 'authenticated' });
            }
        })
    }, []);

    const masquerade = props.masquerade;

    return (
        <Debug.Boundary name={`${__moduleName__}.Session`}>
        <GoogleOAuthProvider {... props.config.services['com.google']?.auth || { clientId: '' }}>
        <AuthContext.Provider value={ process.env.NODE_ENV === 'development' && masquerade ? new class {
            requestLogin(reason: undefined|JSX.Element): void {}
            keep(credentials: Authentication.Credentials): void {}
            validate(): boolean { return true }
            requestLogout(): void {}
            cancelLogout(): void {}
            confirmLogout(): void {}
            mode(): Authentication.Modes { return 'authenticated' }
            credentials(): Partial<Authentication.Credentials> {
                return masquerade
            }
            reason(): undefined|JSX.Element { return undefined; }
        } : new class {
            requestLogin(reason: undefined|JSX.Element): void {
                if(state.credentials.common === undefined && state.mode !== 'login' && state.mode !== 'authenticated') {
                    setState({ mode: 'login', credentials: {} })
                    setWhy(reason);
                }
            }
            keep(credentials: Authentication.Credentials): void {
                setState({ credentials, mode: 'authenticated' });
                props.storage.setItem(accessTokenStorage, JSON.stringify(credentials));
            }
            validate(): boolean {
                return state.mode === 'authenticated' && (state.credentials.apple !== undefined || state.credentials.google !== undefined); // !! should re-validate token here
            }
            requestLogout(): void {
                setState(({credentials}) => ({ credentials, mode: 'logout' }));
            }
            cancelLogout(): void {
                if(state.credentials)
                    setState(({credentials}) => ({ credentials, mode: 'authenticated' }));
                else
                    setState(({credentials}) => ({ credentials, mode: 'notAuthenticated' }));
            }
            confirmLogout(): void {
                setState({ mode: 'notAuthenticated', credentials: {} });
                props.storage.removeItem(accessTokenStorage)
            }
            mode(): Authentication.Modes {
                return state.mode;
            }
            credentials(): Partial<Authentication.Credentials> {
                return state.credentials;
            }
            reason(): undefined|JSX.Element {
                return why;
            }
        }() }>{ props.children }</AuthContext.Provider>
        </GoogleOAuthProvider>
        </Debug.Boundary>
    )
}

export function Protect(props: React.PropsWithChildren<{
    config: Config,
    label: {
        reason: () => JSX.Element,
        login: () => JSX.Element,
        logout: () => JSX.Element,
        cancel: () => JSX.Element
    }
}>) {
    const auth = React.useContext(AuthContext);
    React.useEffect(() => {
        auth?.requestLogin(props.label.reason());
    }, [ auth ]);
    if(!auth?.validate()) {
        return (
            <Modal {...props}/>
        )
    }
    return (
        <Debug.Boundary name={`${__moduleName__}.Protect`}>
            {props.children}
        </Debug.Boundary>
    )
}

export function Modal(props: {
    config: Config,
    label: {
        reason: () => JSX.Element,
        login: () => JSX.Element,
        logout: () => JSX.Element,
        cancel: () => JSX.Element
    }
}) {
    const auth = React.useContext(AuthContext);
    if(!auth) return null;
    if(auth.mode() === 'login') {
        const methods: JSX.Element[] = [];
        const onSignInWithApple = (appleResponse: any) => {
            if(appleResponse.error) {
                //console.log(JSON.stringify(appleResponse.error));
                alert('Sign in with Apple Failed');
            } else {
                const body = JSON.stringify(appleResponse);
                fetch( props.config.services['app.swivell']?.backend.baseURI+'/authentication/auth-with-apple', {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body
                }).then(backendResponse => backendResponse.json())
                .then(json => {
                    auth.keep({
                        apple: {
                            accessCode: appleResponse.authorization.code
                        }, common: {
                            name: json.fullName,
                            email: json.email
                        }
                    });
                }).catch(error => {
                    console.error(JSON.stringify(error));
                    alert('Sign in with Apple Failed');
                })
            }
        };
        const onSignInWithGoogle = (cr: GoogleCredentialResponse) => {
            if(cr.credential) {
                var profile:any = jwt_decode(cr.credential);
                auth.keep({
                    google: {
                        id: profile.sub,
                        accessToken: profile.azp
                    }, common: {
                        name: profile.name,
                        email: profile.email,
                        imageUrl: profile.picture
                    }
                });
            }
        };
        if(props.config.services['com.google']?.auth) {
            methods.push(
                <div id='google-login-div'>
                    <GoogleLogin 
                        onSuccess={onSignInWithGoogle}
                        onError={() => {
                            console.warn('Sign In with Google failed');
                        }}
                    />
                </div>
            )
        }
        if(props.config.services['com.apple']?.auth) {
            methods.push(
                <div id="apple-login-div">
                    <AppleSignin
                        responseMode="query"
                        authOptions={{
                            ... props.config.services['com.apple']?.auth,
                            usePopup: true
                        }}
                        onSuccess={onSignInWithApple}
                        onError={(error:any) => console.error(error)} // default = undefined
                        buttonExtraChildren="with your Apple ID"
                    />
                </div>
            )
        }
        const left = 0;
        const increment = methods.length == 0 ? 0 : 1/methods.length;
        const partitions: JSX.Element[] = methods.map((method, index) => {
            return <div key={`method-${index}`} style={{ display: "flex", flex: "0 0 auto" }}><XLayout.Center.Horizontal>{method}</XLayout.Center.Horizontal></div>;
        })
        
        const why = auth.reason();
        return (
            <Debug.Boundary name="ViewAuthentication.Modal.login">
                <XLayout.Center.Both>
                    <XLayout.Stack.South>
                        <XLayout.Center.Horizontal>{ why ? why : <span>Login</span> }</XLayout.Center.Horizontal>
                        <React.Fragment>
                            { partitions }
                        </React.Fragment>
                    </XLayout.Stack.South>
                </XLayout.Center.Both>
            </Debug.Boundary>
        )
    } else if(auth.mode() === 'logout') {
        return (
            <Debug.Boundary name="ViewAuthentication.Modal.logout">
                <Dialog open={true}>
                    <DialogTitle>Logout?</DialogTitle>
                    <DialogActions>
                        <Button key="confirm-logout" onClick={() => auth.confirmLogout()}>{props.label.logout()}</Button>
                        <Button key="cancel-logout" onClick={() => auth.cancelLogout()}>{props.label.cancel()}</Button>
                    </DialogActions>
                </Dialog>
            </Debug.Boundary>
        );
    }
    return <React.Fragment></React.Fragment>;
}

export function AsTile(props: {
    pixelSize: {
        height: number,
        width: number
    }
}) {
    const auth = React.useContext(AuthContext);
    if(!auth) return null;
    const imageUrl = auth.credentials().common?.imageUrl;
    return (
        <div style={ { ...props.pixelSize, marginBottom: '2px' }}>
            { imageUrl && <img style={{ maxHeight: '100%', maxWidth: '100%' }} src={imageUrl}></img> }
            { imageUrl === undefined && <MuiIcons.Person/>}
        </div>
    );
}

export function withCredentials(
    render: (creds: Partial<Authentication.Credentials>) => JSX.Element
): JSX.Element {
    const context = React.useContext(AuthContext);
    const credentials: Partial<Authentication.Credentials> = 
        context === undefined 
        ? {} 
        : context.credentials();
    return render(credentials);
}
